<template>
  <div class="card list recents" :class="{ desktop: $isDesktop }">
    <translate tag="h3">Recent searches</translate>
    <ul class="cards">
      <template v-if="recentSearches?.length">
        <li v-for="search of recentSearches" :key="search.timestamp">
          <nuxt-link
            :to="$puLocalePath(search.url)"
            @click="toggleAccountMenu()"
          >
            <span class="title" v-if="search.meta.filters.q">{{
              search.meta.filters.q
            }}</span>
            <span class="title" v-else-if="search.meta.filters.placename">{{
              search.meta.filters.placename
            }}</span>
            <translate
              class="title"
              v-else-if="
                search.meta.filters.area?.lat && search.meta.filters.area?.lng
              "
              >My location</translate
            >
            <translate class="title" v-else-if="search.meta.filters.bounds"
              >Custom map area</translate
            >

            <span
              class="title"
              v-else-if="search.meta.search_context?.heading"
              >{{ search.meta.search_context.heading }}</span
            >
            <section class="search-criteria">
              <p v-if="search.meta.filters.within" class="radius">
                {{ search.meta.filters.within }} (m or km)
              </p>
              <translate
                v-else-if="
                  !search?.isEmpty &&
                  !search.meta.filters.area?.lat &&
                  !search.meta.filters.area?.lng &&
                  !search.meta.search_context?.hierarchyName &&
                  !search.meta.filters?.bounds
                "
                tag="p"
              >
                All locations
              </translate>
              <pu-category-icons
                v-if="search.meta.filters.categoryIds?.length"
                :categories="getCategories(search.meta.filters.categoryIds)"
                class="category-icons"
              />

              <div class="search-meta dates" v-if="search.meta.filters.dates">
                <img
                  src="@/assets/images/calendar-blue.svg"
                  width="14"
                  height="19"
                  alt=""
                />
                <p>
                  <template
                    v-if="
                      isSameMonth(
                        search.meta.filters.dates.arrive,
                        search.meta.filters.dates.depart,
                      )
                    "
                  >
                    <search-summary-bar-details-calendar-same-month
                      :date-range="{
                        arrive: search.meta.filters.dates?.arrive,
                        depart: search.meta.filters.dates?.depart,
                      }"
                    />
                  </template>
                  <template v-else>
                    <i18n-t keypath="{fromDate} - {toDate}">
                      <template #fromDate>
                        <pu-utc-date
                          :date="search.meta.filters.dates?.arrive"
                          options="dayMonth"
                        />
                      </template>
                      <template #toDate>
                        <pu-utc-date
                          :date="search.meta.filters.dates?.depart"
                          options="dayMonth"
                        />
                      </template>
                    </i18n-t>
                  </template>
                  <pu-date
                    tag="span"
                    class="year"
                    :date="search.meta.filters.dates?.depart"
                    options="year"
                  />
                </p>
              </div>

              <div
                class="search-meta party"
                v-if="
                  search.meta.filters.party.adults ||
                  search.meta.filters.party?.childAges.length
                "
              >
                <img
                  src="@/assets/images/adults.svg"
                  width="10"
                  height="15"
                  alt=""
                />
                <p>
                  <!-- Adults + Children + Dogs -->
                  <translate
                    v-if="
                      search.meta.filters.party?.childAges.length > 0 &&
                      search.meta.filters.party?.dogs
                    "
                    :tt="{
                      adults: search.meta.filters.party.adults,
                      childrenCount:
                        search.meta.filters.party?.childAges.length,
                      dogs: search.meta.filters.party?.dogs,
                    }"
                  >
                    {adults} adults, {childrenCount} children, {dogs} dogs
                  </translate>
                  <!-- Adults + Children (no dogs) -->
                  <translate
                    v-else-if="search.meta.filters.party?.childAges.length > 0"
                    :tt="{
                      adults: search.meta.filters.party.adults,
                      childrenCount:
                        search.meta.filters.party?.childAges.length,
                    }"
                  >
                    {adults} adults, {childrenCount} children
                  </translate>
                  <!-- Adults + Dogs (no children) -->
                  <translate
                    v-else-if="search.meta.filters.party?.dogs"
                    :tt="{
                      adults: search.meta.filters.party.adults,
                      dogs: search.meta.filters.party?.dogs,
                    }"
                  >
                    {adults} adults, {dogs} dogs
                  </translate>
                  <!-- Adults only -->
                  <translate
                    v-else
                    :tt="{ adults: search.meta.filters.party.adults }"
                  >
                    {adults} adults
                  </translate>
                </p>
              </div>

              <div
                class="search-meta price"
                v-if="
                  search.meta.filters.priceRange?.max ||
                  search.meta.filters.priceRange?.min
                "
              >
                <img
                  src="@/assets/currency-symbols/dollar.svg"
                  width="19"
                  height="19"
                  alt=""
                />
                <!-- TODO wire actual price currency symbols -->
                <p>
                  <i18n-t
                    v-if="
                      search.meta.filters.priceRange.min &&
                      !search.meta.filters.priceRange.max
                    "
                    keypath="Min: {minPrice}"
                  >
                    <template #minPrice>
                      {{ getRoundedPrice(search.meta.filters.priceRange.min) }}
                    </template>
                  </i18n-t>
                  <i18n-t
                    v-if="
                      search.meta.filters.priceRange.max &&
                      !search.meta.filters.priceRange.min
                    "
                    keypath="Max: {maxPrice}"
                  >
                    <template #maxPrice>
                      {{ getRoundedPrice(search.meta.filters.priceRange.max) }}
                    </template>
                  </i18n-t>
                  <i18n-t
                    v-if="
                      search.meta.filters.priceRange.min &&
                      search.meta.filters.priceRange.max
                    "
                    keypath="{minPrice} - {maxPrice}"
                  >
                    <template #minPrice>
                      {{ getRoundedPrice(search.meta.filters.priceRange.min) }}
                    </template>
                    <template #maxPrice>
                      {{ getRoundedPrice(search.meta.filters.priceRange.max) }}
                    </template>
                  </i18n-t>
                </p>
              </div>

              <template v-if="search.meta.filters.facets.length">
                <template
                  v-if="search.meta.filters.facets.includes('dogs-allowed')"
                >
                  <div class="search-meta facets">
                    <img
                      src="@/assets/images/dog-blue.svg"
                      width="20"
                      height="20"
                      alt=""
                    />
                    <translate tag="p">Dogs allowed</translate>
                  </div>
                  <div
                    class="search-meta facets"
                    v-if="search.meta.filters.facets.length > 1"
                  >
                    <img
                      src="@/assets/images/filter-blue.svg"
                      width="14"
                      height="19"
                      alt=""
                    />
                    <p>
                      +<translate
                        :tt="{ count: search.meta.filters.facets.length - 1 }"
                        >{count} applied filters</translate
                      >
                    </p>
                  </div>
                </template>
                <div v-else class="search-meta facets">
                  <img
                    src="@/assets/images/filter-blue.svg"
                    width="14"
                    height="19"
                    alt=""
                  />
                  <p>
                    +<translate
                      :tt="{ count: search.meta.filters.facets.length }"
                      >{count} applied filters</translate
                    >
                  </p>
                </div>
              </template>
            </section>

            <time-ago class="time-ago" :date="search.timestamp"
              >Searched
            </time-ago>
          </nuxt-link>
        </li>
      </template>
      <li v-else class="empty">
        <div class="search-criteria">
          <translate>Your latest searches will show here.</translate>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-unsafe-return */

/* TODO: Add proper typing to resolve type safety warnings */

import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { isUndefined } from 'lodash-es'

const { $isDesktop, $puLocalePath, $i18n } = useNuxtApp()
const appMenuStore = useAppMenuStore(usePinia())

const route = useRoute()
const presenter = getCampsiteCategoriesPresenter(
  (route.params.lang as string) || 'en-gb',
)

const { data: recentSearches } = useAsyncData(
  () =>
    $fetch('/_/fallback/search2/history/?limit=10', {
      method: 'GET',
    }).then((response) => response.results),
  {
    server: false,
  },
)

function isSameMonth(arrive, depart) {
  const arriveDate = new Date(arrive)
  const departDate = new Date(depart)
  return arriveDate.getMonth() === departDate.getMonth()
}

function getCategories(categoryIds: number[]) {
  if (!categoryIds?.length) return []
  return categoryIds
    .filter((id) => presenter.byIdMap[id])
    .map((id) => presenter.byIdMap[id])
}

function toggleAccountMenu() {
  if (!$isDesktop) {
    appMenuStore.toggleAccountMenu()
  }
}

function getRoundedPrice(price: number) {
  if (isUndefined(price)) return
  return new Intl.NumberFormat($i18n.locale).format(Math.round(price))
}
</script>

<style scoped>
.card.list.recents {
  background: transparent;
  box-shadow: none;
  padding: 0;
  overflow: hidden;
  margin: 2em 0 0 0;
  width: 100%;
}

.cards {
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 1.4em 2em;
  scroll-padding: 0 1.4em;
  scroll-snap-type: x mandatory;
  margin: 0.5em 0 0 0;
  display: flex;
  align-items: stretch;
  gap: 1em;
  justify-content: flex-start;
  list-style: none;
  line-height: 1.6;
}

.cards li a {
  background: white;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  box-shadow: var(--card-box-shadow);
  border-radius: 0.4em;
  padding: 0.7em 1em 0.4em;
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  position: relative;
  text-decoration: none;
  height: 100%;
}

.cards li a:before {
  content: '';
  background: white;
  width: 1em;
  height: 3em;
  position: absolute;
  left: -0.6em;
  top: 0;
  bottom: 0;
  border-radius: 0.4em 0 0 1em;
}

.cards li a:after {
  content: url('@/assets/images/magnify-blue.svg');
  width: 0.9em;
  filter: brightness(0.5);
  margin-top: 0.15em;
  position: absolute;
  left: -0.3em;
}

.cards li.empty a {
  min-width: 40vw;
  max-width: 100%;
}

.empty {
  width: 100%;
}

.empty .search-criteria {
  padding-top: 0.3em;
  font-size: var(--body);
  min-height: 3em;
}

.cards li .row {
  align-items: center;
  gap: 0.3em;
  justify-content: flex-start;
}

.cards li span.row {
  font-size: inherit;
}

.cards .title {
  color: var(--colour-dark-blue);
  display: block;
  font-size: var(--title);
  font-weight: 500;
  margin: 0.2em 0 0.25em;
  max-width: 20ch;
  min-width: 15ch;
  text-wrap-style: pretty;
}

.search-criteria {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.search-criteria img {
  display: inline-block;
  height: 1em;
  width: fit-content;
}

.search-criteria p {
  padding: 0;
  margin: 0;
  font-size: var(--body);
  font-weight: 300;
}

.search-criteria :deep(.category-icons) {
  justify-content: start;
  margin-top: 0.1em;
}

.search-criteria :deep(.category-icons img) {
  height: 0.9em;
}

.search-meta {
  margin: 0.25em 0 0 0;
  display: grid;
  grid-template-columns: 0.75em 1fr;
  place-items: center start;
  gap: 0.25em;
}

.search-meta p {
  white-space: nowrap;
}

.search-meta.facets img {
  align-self: self-start;
}

.search-meta.party img {
  filter: saturate(0.25) hue-rotate(98deg) brightness(1.8);
  justify-self: center;
  height: 0.8em;
}

.search-meta.party :deep(strong) {
  font-weight: 300;
}

.time-ago {
  color: var(--colour-text-light);
  font-size: var(--small);
  margin-top: auto;
  padding-top: 0.85em;
  align-self: flex-start;
  color: var(--colour-blue);
  justify-self: flex-end;
  display: flex;
  align-items: center;
  gap: 0.2em;
}

.time-ago:before {
  content: '';
  display: inline-block;
  width: 1em;
  height: 1em;
  flex-shrink: 0;
  align-self: start;
  background: url('~/assets/images/time.svg') no-repeat bottom left;
  margin-top: 0.1em;
}

@supports (scrollbar-width: none) {
  .cards {
    scrollbar-width: none;
  }
}

/* desktop & tablet */
.card.list.recents.desktop {
  grid-column: 1 / -1;
}

.card.list.recents.desktop .cards .title {
  min-width: 18ch;
  max-width: 26ch;
}

.card.list.recents.desktop {
  overflow: visible;
}

.card.list.recents.desktop .cards {
  gap: 1.5em;
  padding-left: 0.75em;
}

.recents.desktop .cards li a {
  min-width: max-content;
}

@media (hover: hover) {
  .recents.desktop .cards li a:hover {
    background: var(--colour-page-background-light);
  }

  .recents.desktop .cards li a:hover:before {
    background: var(--colour-page-background-light);
  }

  .recents.desktop .cards li a,
  .recents.desktop .cards li a:before {
    transition: background 0.15s ease-in;
  }
}
</style>
